import { useCallback, useEffect, useState } from "react";
import {
  friendlyError,
  partnerApi,
  type CreatePayoutIn,
  type PartnerAccrual,
  type PartnerMe,
  type PartnerPayout,
  type PartnerPayoutMethod,
} from "@/lib/api";
import { Button, Card, ErrorText, Field, PageLoader, Row, Badge, usd, rub, fmtDate } from "@/components/ui";

const METHOD_LABEL: Record<PartnerPayoutMethod, string> = {
  internal: "На свой счёт",
  crypto: "Крипто-кошелёк",
  card: "Зарубежная карта",
  swift: "SWIFT-перевод",
};

const ACCRUAL_LABEL: Record<PartnerAccrual["status"], { label: string; tone: "green" | "amber" | "red" | "muted" }> = {
  pending: { label: "Ожидает сделки", tone: "muted" },
  hold: { label: "В холде", tone: "amber" },
  available: { label: "Доступно", tone: "green" },
  cancelled: { label: "Отменено", tone: "red" },
};

const PAYOUT_LABEL: Record<PartnerPayout["status"], { label: string; tone: "green" | "amber" | "red" | "muted" }> = {
  pending: { label: "Ожидает", tone: "amber" },
  completed: { label: "Исполнена", tone: "green" },
  rejected: { label: "Отклонена", tone: "red" },
  cancelled: { label: "Отменена", tone: "muted" },
};

export function Finance() {
  const [me, setMe] = useState<Extract<PartnerMe, { is_partner: true }> | null>(null);
  const [tab, setTab] = useState<"payout" | "history">("payout");
  const [accruals, setAccruals] = useState<PartnerAccrual[]>([]);
  const [payouts, setPayouts] = useState<PartnerPayout[]>([]);
  const [err, setErr] = useState("");

  const loadMe = useCallback(() => {
    partnerApi
      .me()
      .then((m) => {
        if (m.is_partner) setMe(m);
        else setErr("Финансы доступны после одобрения заявки.");
      })
      .catch((e) => setErr(friendlyError(e)));
  }, []);
  const reloadHistory = useCallback(() => {
    partnerApi.accruals().then((r) => setAccruals(r.accruals)).catch(() => setAccruals([]));
    partnerApi.payouts().then((r) => setPayouts(r.payouts)).catch(() => setPayouts([]));
  }, []);

  useEffect(() => {
    loadMe();
    reloadHistory();
  }, [loadMe, reloadHistory]);

  if (err) return <Card className="mt-4"><ErrorText>{err}</ErrorText></Card>;
  if (!me) return <PageLoader />;

  return (
    <div className="space-y-3 pt-2">
      <Card>
        <div className="text-xs text-white/45">Доступно</div>
        <div className="mt-1 text-3xl font-semibold tabular-nums">{usd(me.balance.available_usd)}</div>
        <div className="mt-3">
          <Row left={<span className="text-white/50">В холде</span>} right={usd(me.balance.hold_usd)} />
          <Row left={<span className="text-white/50">Заработано всего</span>} right={usd(me.balance.total_earned_usd)} />
        </div>
      </Card>

      <div className="flex gap-2">
        <TabButton active={tab === "payout"} onClick={() => setTab("payout")}>Вывести</TabButton>
        <TabButton active={tab === "history"} onClick={() => setTab("history")}>История</TabButton>
      </div>

      {tab === "payout" ? (
        <PayoutForm
          me={me}
          onDone={() => {
            loadMe();
            reloadHistory();
            setTab("history");
          }}
        />
      ) : (
        <History accruals={accruals} payouts={payouts} onCancelled={() => { loadMe(); reloadHistory(); }} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
        active ? "bg-brand text-surface-base" : "bg-surface-raised text-white/60"
      }`}
    >
      {children}
    </button>
  );
}

function PayoutForm({
  me,
  onDone,
}: {
  me: Extract<PartnerMe, { is_partner: true }>;
  onDone: () => void;
}) {
  const [method, setMethod] = useState<PartnerPayoutMethod>("crypto");
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [swift, setSwift] = useState({ beneficiary_name: "", iban: "", swift: "", bank_name: "", bank_country: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const v = me.verification;
  // «На свой счёт» (internal) временно скрыт: он кредитует локальный ₽-баланс без соответствующих
  // рублей у брокера и ломает инвариант «локальный ₽ = ₽ у брокера».
  const allowed: PartnerPayoutMethod[] = [];
  if (v.can_payout_crypto) allowed.push("crypto");
  if (v.can_payout_fiat) allowed.push("card", "swift");

  const value = Number(amount.replace(",", "."));
  const min = me.program.min_payout_usd;
  const fee = me.program.payout_fees_usd?.[method] ?? 0;
  const enough =
    allowed.includes(method) &&
    Number.isFinite(value) && value > 0 && value <= me.balance.available_usd && value >= min;

  const submit = async () => {
    if (!enough || busy) return;
    setBusy(true);
    setError("");
    const body: CreatePayoutIn = { method, amount_usd: value };
    if (method === "crypto") body.crypto = { wallet_address: wallet.trim() };
    if (method === "card") body.card = { card_number: cardNumber, first_name: firstName, last_name: lastName };
    if (method === "swift") {
      body.swift = {
        beneficiary_name: swift.beneficiary_name,
        iban: swift.iban,
        swift_bic: swift.swift,
        bank_name: swift.bank_name,
        bank_country: swift.bank_country,
      };
    }
    try {
      await partnerApi.createPayout(body);
      setAmount("");
      onDone();
    } catch (e) {
      setError(friendlyError(e, "Не удалось создать заявку на выплату."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {allowed.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMethod(m)}
            className={`rounded-2xl border p-3 text-left text-sm transition ${
              method === m ? "border-brand/60 bg-brand/10" : "border-white/5 bg-surface-card text-white/70"
            }`}
          >
            {METHOD_LABEL[m]}
          </button>
        ))}
      </div>

      {!v.can_payout_crypto && (
        <Card className="border-amber-400/20 bg-amber-400/5">
          <p className="text-sm text-amber-200/80">
            Чтобы выводить вознаграждение, пройдите проверку личности.
          </p>
        </Card>
      )}
      {v.can_payout_crypto && !v.can_payout_fiat && (
        <Card>
          <p className="text-sm text-white/55">
            Вывод в рублях и валюте откроется после полной верификации с брокером. Крипта доступна
            уже сейчас.
          </p>
        </Card>
      )}

      <div>
        <Field
          label="Сумма, $"
          type="number"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={
            method === "internal"
              ? "Зачислим на ваш рублёвый счёт по базовому курсу, без наценки"
              : `Минимум ${me.program.min_payout_usd} $${fee ? `, комиссия ${fee} $` : ""}`
          }
        />
        <button
          type="button"
          className="mt-1 text-xs text-brand disabled:text-white/30"
          disabled={me.balance.available_usd <= 0}
          onClick={() => setAmount(String(Math.floor(me.balance.available_usd * 100) / 100))}
        >
          Максимум {usd(Math.max(0, me.balance.available_usd))}
        </button>
      </div>

      {method === "crypto" && (
        <Field label="Кошелёк TRC20" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="T..." />
      )}

      {method === "card" && (
        <>
          <Field label="Номер карты" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} />
          <Field label="Имя (латиницей)" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Field label="Фамилия (латиницей)" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </>
      )}

      {method === "swift" && (
        <>
          <Field label="Получатель (латиницей)" value={swift.beneficiary_name} onChange={(e) => setSwift((s) => ({ ...s, beneficiary_name: e.target.value }))} />
          <Field label="IBAN или номер счёта" value={swift.iban} onChange={(e) => setSwift((s) => ({ ...s, iban: e.target.value }))} />
          <Field label="SWIFT / BIC" value={swift.swift} onChange={(e) => setSwift((s) => ({ ...s, swift: e.target.value }))} />
          <Field label="Банк" value={swift.bank_name} onChange={(e) => setSwift((s) => ({ ...s, bank_name: e.target.value }))} />
          <Field label="Страна банка" value={swift.bank_country} onChange={(e) => setSwift((s) => ({ ...s, bank_country: e.target.value }))} />
        </>
      )}

      <ErrorText>{error}</ErrorText>
      <Button className="w-full" loading={busy} disabled={!enough} onClick={submit}>
        {method === "internal" ? "Перевести на счёт" : "Запросить выплату"}
      </Button>
    </div>
  );
}

function History({
  accruals,
  payouts,
  onCancelled,
}: {
  accruals: PartnerAccrual[];
  payouts: PartnerPayout[];
  onCancelled: () => void;
}) {
  const [cancelling, setCancelling] = useState<string | null>(null);
  const cancel = async (id: string) => {
    setCancelling(id);
    try {
      await partnerApi.cancelPayout(id);
      onCancelled();
    } catch {
      /* ignore */
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="space-y-3">
      {payouts.length > 0 && (
        <div className="space-y-2">
          <div className="text-base font-semibold">Выплаты</div>
          {payouts.map((p) => (
            <Card key={p.id}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{METHOD_LABEL[p.method]}</span>
                <span className="text-sm font-semibold">{usd(p.net_usd)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-white/40">
                <span>{p.reference}</span>
                <Badge tone={PAYOUT_LABEL[p.status].tone}>{PAYOUT_LABEL[p.status].label}</Badge>
              </div>
              {p.method === "internal" && p.amount_rub !== null && (
                <div className="mt-1 text-xs text-white/40">
                  Зачислено {rub(p.amount_rub)} по курсу {p.rate?.toFixed(2)}
                </div>
              )}
              {p.reject_reason && <div className="mt-1 text-xs text-red-400">{p.reject_reason}</div>}
              {p.status === "pending" && (
                <button
                  type="button"
                  className="mt-2 text-xs text-red-300 disabled:opacity-50"
                  disabled={cancelling === p.id}
                  onClick={() => cancel(p.id)}
                >
                  {cancelling === p.id ? "Отмена…" : "Отменить"}
                </button>
              )}
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="text-base font-semibold">Начисления</div>
        {accruals.length === 0 && (
          <Card><p className="text-sm text-white/50">Начислений пока нет.</p></Card>
        )}
        {accruals.map((a) => (
          <Card key={a.id}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">{a.level === "L2" ? "Второй уровень" : "Сделка клиента"}</span>
              <span className="text-sm font-semibold">{usd(a.amount_usd)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-white/40">
              <Badge tone={ACCRUAL_LABEL[a.status].tone}>
                {ACCRUAL_LABEL[a.status].label}
                {a.status === "hold" && a.available_at ? ` до ${fmtDate(a.available_at)}` : ""}
              </Badge>
              <span>{rub(a.turnover_rub)}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
